/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useEffect, useMemo, useState } from 'react';
import { isEmpty, isEqual, isNull, isUndefined } from 'lodash';
import { useNavigate, useParams } from 'react-router-dom';
import { ERROR_403 } from 'common/config/api';
import { forbiddenPath, notFoundPath } from 'common/config/urls';
import {
  COMP_GROUPS_SLUG,
  FIRM_REQUESTS_SLUG,
  FIRM_SETTINGS_SLUG,
  FIRMS_SUMMARY_SLUG,
  FORBIDDEN_FIRM_SUMMARY,
  GET_FIRM_SUMMARY_ACCESS,
  SCIM_CONFIGURATION_TAB,
  SINGLE_SIGN_ON_TAB,
} from 'common/constants/firms';
import { BULK_ACTIONS, EXCEL_EXPORT, PDF_EXPORT } from 'common/constants/pageActions';
import { FIRM_TYPE } from 'common/constants/pageTypes';
import { FIRM_WATERFALL, USER_MANAGEMENT_TAB } from 'common/constants/user';
import { useStore } from 'common/store';
import { Firm } from 'common/types/firm';
import { MenuAndSubMenu, UseStoreValues } from 'common/types/store';
import { MessageBox, PdfExportConfigDialog } from 'components';
import useCheckFeatures from 'components/FeatureEnabler/hooks';
import LayoutContext from 'context/LayoutContext';
import { UserPermissions as UserPermissionsView } from 'pages';
import CompGroups from 'pages/CompGroups/components/CompGroups';
import { CompGroup } from 'pages/CompGroups/components/CompGroups/CompGroups.props';
import { PRIVATE_TRANSACTIONS } from 'pages/CompGroups/constants/constants';
import FirmWaterfall from 'pages/Firms/FirmWaterfall';
import { PUBLIC_COMPANIES } from 'pages/Valuations/util/constants';
import { useResponse } from 'services/hooks';
import { useCreateOrUpdateCompGroups, useGetFundByFirmId } from 'services/hooks/firm';
import { FIRM_ROUTES } from './firm.routes';
import { ExpandedState, RouteParams, TransactionsValidatedGridsState } from './Firms.props';
import Settings from './FirmSettings';
import { FirmSummary, ScimConfiguration, SingleSignOn } from './pages';
import Requests from './Requests/FirmRequests';
import generateCompGroupsRequest from './utilities/generateCompGroupsRequest';

const Firms = () => {
  const { errorNotification } = useResponse();
  const { firmSlugParam, tableSlugParam } = useParams<RouteParams>();
  const navigate = useNavigate();
  const [{ firmList, firmId, user }] = useStore() as unknown as UseStoreValues;
  const [fundsSummary, getFundByFirmId] = useGetFundByFirmId();
  const [GPCCompsGroups, setGPCCompsGroups] = useState<CompGroup[]>();
  const [transactionCompsGroups, setTransactionCompsGroups] = useState<CompGroup[]>();
  const [updatedCompGroups, createOrUpdateCompGroups] = useCreateOrUpdateCompGroups();
  const [features, checkFeature] = useCheckFeatures();

  const [compsToDelete, setCompsToDelete] = useState<CompGroup[]>([]);
  // accordions expanded state
  const [expandedState, setExpandedState] = useState<ExpandedState>({});
  const [transactionsValidatedGrids, setTransactionsValidatedGrids] = useState<TransactionsValidatedGridsState>({});
  const [showTransactionCellsWithErrors, setShowTransactionCellsWithErrors] = useState<boolean>(false);

  const {
    setNavItems,
    setPageTitle,
    setPageType,
    updatePageActions,
    setPageBreadcrumbs,
    toggleExportDialog,
    toggleBulkActionsDialog,
    togglePdfDialog,
    openPdfDialog,
    updateExtraPageActions,
    viewOnlyUser,
  } = useContext(LayoutContext);

  const activeFirm = useMemo(
    () =>
      isEmpty(firmList) || (firmList as unknown as string) === ERROR_403 || !firmSlugParam
        ? null
        : (firmList as Firm[]).find(item => item.slug === firmSlugParam),
    [firmList, firmSlugParam]
  );

  const renderSuperuserPage = useMemo(
    () => user?.is_superuser && firmSlugParam === '0' && tableSlugParam === 'comp-groups',
    [user, firmSlugParam, tableSlugParam]
  );

  const navigateToUrl = (
    getUrl: ((firmSlug: string, companySlug: string) => string) | ((firmSlug: string) => string)
  ) => {
    if (firmSlugParam) {
      // Check if the function expects two parameters (company routes) or one parameter (firm routes)
      if (getUrl.length === 2) {
        navigate((getUrl as (firmSlug: string, companySlug: string) => string)(firmSlugParam, ''));
      } else {
        navigate((getUrl as (firmSlug: string) => string)(firmSlugParam));
      }
    }
  };

  const menuItems: MenuAndSubMenu[] = useMemo(() => {
    // Filter routes based on feature flags and user permissions
    const filteredRoutes = FIRM_ROUTES.filter(route => {
      // Check feature flag if present
      if (route.featureFlag && !checkFeature(route.featureFlag)) {
        return false;
      }
      return true;
    });

    // Handle special cases for different user types
    if (renderSuperuserPage) {
      return filteredRoutes
        .filter(route => route.id === 'comp-groups')
        .map(route => ({
          id: route.id,
          slug: route.slug,
          title: route.title,
          itemKey: route.itemKey!,
          callback: () => navigateToUrl(route.route),
        }));
    }

    if (viewOnlyUser) {
      return filteredRoutes
        .filter(route => route.id === 'summary')
        .map(route => ({
          id: route.id,
          slug: route.slug,
          title: route.title,
          itemKey: route.itemKey!,
          callback: () => navigateToUrl(route.route),
        }));
    }

    // Return all routes for normal users
    return filteredRoutes.map(route => ({
      id: route.id,
      slug: route.slug,
      title: route.title,
      itemKey: route.itemKey!,
      callback: () => navigateToUrl(route.route),
    }));
  }, [firmSlugParam, renderSuperuserPage, features, viewOnlyUser]);

  const saveCompGroups = (areCellsValid: boolean): void => {
    if (!areCellsValid) {
      return;
    }
    const compGroupsList = [...(GPCCompsGroups || []), ...(transactionCompsGroups || [])];

    const validCompGroups = renderSuperuserPage ? compGroupsList : compGroupsList.filter(({ is_global }) => !is_global);
    const validRequestBody = generateCompGroupsRequest(validCompGroups, compsToDelete, renderSuperuserPage);
    const firmIdentifier = renderSuperuserPage ? 0 : firmId;
    createOrUpdateCompGroups?.(firmIdentifier ?? 0, validRequestBody);
  };

  const handleValidate = (): void => {
    const compGroupsList = [...(GPCCompsGroups || []), ...(transactionCompsGroups || [])];
    const allCompGroupsHaveValidNames = compGroupsList.every(({ name }) => name !== '');
    const allGPCCompGroupsHaveData = GPCCompsGroups?.every(group => group.gpc_comps?.length);
    const allPTCompGroupsHaveData = transactionCompsGroups?.every(group => group.pt_comps?.length);

    if (allCompGroupsHaveValidNames && allGPCCompGroupsHaveData && allPTCompGroupsHaveData) {
      if (Object.values(transactionsValidatedGrids).every(({ isValid }) => isValid)) {
        saveCompGroups(true);
      } else {
        setShowTransactionCellsWithErrors(true);
        errorNotification('Check that the transactions table has valid data');
      }
    } else {
      errorNotification('Check that the comp groups have valid names and are not empty');
    }
  };

  const handleSave = (): void => {
    if (!isEmpty(GPCCompsGroups) || !isEmpty(transactionCompsGroups) || !isEmpty(compsToDelete)) {
      handleValidate();
    } else {
      errorNotification('You must add at least one comp group to save.');
    }
  };

  useEffect(() => {
    if (!isEmpty(firmList) && tableSlugParam === 'comp-groups') {
      updatePageActions?.({
        mainAction: {
          id: 'firms',
          label: 'Save',
          callback: handleSave,
        },
      });
    }

    return () => {
      updatePageActions?.(null);
    };
  }, [firmList, firmId, GPCCompsGroups, transactionCompsGroups, transactionsValidatedGrids, updatePageActions]);

  useEffect(() => {
    if (!isEmpty(activeFirm)) {
      if (activeFirm.name) {
        setPageTitle?.(activeFirm.name);
      }

      if (activeFirm.id) {
        getFundByFirmId(activeFirm.id);
      }
    }
    if (isUndefined(activeFirm) && renderSuperuserPage) {
      setPageTitle?.('Global comp groups');
    }

    return () => {
      setPageTitle?.(null);
      setPageType?.(FIRM_TYPE);
    };
  }, [activeFirm, renderSuperuserPage]);

  useEffect(() => {
    const extraActions = [
      {
        label: EXCEL_EXPORT,
        callback: toggleExportDialog,
        isActive: true,
      },
      {
        label: BULK_ACTIONS,
        callback: toggleBulkActionsDialog,
        isActive: true,
      },
    ];

    // Add PDF export action for pages that support it
    extraActions.push({
      label: PDF_EXPORT,
      callback: togglePdfDialog,
      isActive: true,
    });

    updateExtraPageActions?.(extraActions);
  }, [updateExtraPageActions, toggleBulkActionsDialog, togglePdfDialog]);

  useEffect(() => {
    setNavItems?.(menuItems);
    return () => setNavItems?.(null);
  }, [menuItems]);

  const formatSlug = (slug: string): string => {
    const slugArray = slug.split('-');
    return slugArray.map(item => item.charAt(0).toUpperCase() + item.slice(1)).join(' ');
  };

  useEffect(() => {
    if (updatedCompGroups && !isUndefined(renderSuperuserPage)) {
      const filterCriteria = (group_type: string, requiredType: string, is_global: boolean) =>
        group_type === requiredType && (renderSuperuserPage ? is_global : !is_global);
      setGPCCompsGroups(
        updatedCompGroups.filter(({ group_type, is_global }) => filterCriteria(group_type, PUBLIC_COMPANIES, is_global))
      );
      setTransactionCompsGroups(
        updatedCompGroups.filter(({ group_type, is_global }) =>
          filterCriteria(group_type, PRIVATE_TRANSACTIONS, is_global)
        )
      );
      setCompsToDelete([]);
    }
    return () => {
      setGPCCompsGroups(undefined);
      setTransactionCompsGroups(undefined);
    };
  }, [updatedCompGroups, renderSuperuserPage]);

  const breadcrumbs = useMemo(
    () => [
      {
        title: activeFirm?.name ?? 'Admin',
      },
      {
        title: formatSlug(tableSlugParam),
      },
    ],
    [activeFirm, tableSlugParam]
  );

  useEffect(() => {
    setPageBreadcrumbs?.(breadcrumbs);
  }, [activeFirm, tableSlugParam]);

  const compGroupsProps = {
    GPCCompsGroups,
    setGPCCompsGroups,
    transactionCompsGroups,
    setTransactionCompsGroups,
    setCompsToDelete,
    expandedState,
    setExpandedState,
    setTransactionsValidatedGrids,
    showTransactionCellsWithErrors,
  };

  if (renderSuperuserPage) {
    return <CompGroups {...compGroupsProps} firmId={0} />;
  }

  if (activeFirm && isEmpty(activeFirm)) {
    navigate(notFoundPath);
    return null;
  }

  if (isUndefined(activeFirm) && !isNull(user)) {
    navigate(forbiddenPath);
    return null;
  }

  if (activeFirm?.id && isEqual(fundsSummary, ERROR_403)) {
    return <MessageBox title={FORBIDDEN_FIRM_SUMMARY} tagline={GET_FIRM_SUMMARY_ACCESS} fullWidth={false} />;
  }

  const renderCurrentView = () => {
    switch (tableSlugParam) {
      case FIRMS_SUMMARY_SLUG:
        return <FirmSummary />;
      case COMP_GROUPS_SLUG:
        return <CompGroups {...compGroupsProps} firmId={firmId as number} />;
      case FIRM_SETTINGS_SLUG:
        return <Settings />;
      case FIRM_REQUESTS_SLUG:
        return <Requests />;
      case FIRM_WATERFALL:
        return <FirmWaterfall />;
      case USER_MANAGEMENT_TAB:
        return <UserPermissionsView />;
      case SINGLE_SIGN_ON_TAB:
        return <SingleSignOn />;
      case SCIM_CONFIGURATION_TAB:
        return <ScimConfiguration />;
      default:
        return null;
    }
  };

  return (
    <>
      {renderCurrentView()}
      <PdfExportConfigDialog open={openPdfDialog} onClose={togglePdfDialog} />
    </>
  );
};

export default Firms;
